import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, lazy, Suspense } from 'react';
import useAuthStore from './store/useAuthStore';
import { registerSessionExpiredHandler } from './utils/sessionAuth';
import { getStoredToken } from './utils/safeStorage';
import Layout from './components/Layout';
import ShiftGuard from './components/ShiftGuard';
import FeedGuard from './components/FeedGuard';
import BootstrapRecovery from './components/BootstrapRecovery';
import InAppBrowserHint from './components/InAppBrowserHint';
import { reportBootEvent } from './utils/bootDiagnostics';
import { shouldUseFastBoot } from './utils/inAppBrowser';
import { useAppBootMonitor, markMilestone } from './hooks/useAppBootMonitor';
import { clearBootParamsFromUrl } from './utils/reloadGuard';

const Login = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Shifts = lazy(() => import('./pages/Shifts'));
const Payment = lazy(() => import('./pages/Payment'));
const Requests = lazy(() => import('./pages/Requests'));
const Repairs = lazy(() => import('./pages/Repairs'));
const Incidents = lazy(() => import('./pages/Incidents'));
const Garbage = lazy(() => import('./pages/Garbage'));
const Toilets = lazy(() => import('./pages/Toilets'));
const Snow = lazy(() => import('./pages/Snow'));
const Participants = lazy(() => import('./pages/Participants'));
const Staff = lazy(() => import('./pages/Staff'));
const Property = lazy(() => import('./pages/Property'));
const Mounting = lazy(() => import('./pages/Mounting'));
const ORMM = lazy(() => import('./pages/ORMM'));
const Animation = lazy(() => import('./pages/Animation'));
const Plants = lazy(() => import('./pages/Plants'));
const Deratization = lazy(() => import('./pages/Deratization'));
const Approvals = lazy(() => import('./pages/Approvals'));
const Visitors = lazy(() => import('./pages/Visitors'));
const Decor = lazy(() => import('./pages/Decor'));
const Journal = lazy(() => import('./pages/Journal'));
const Menu = lazy(() => import('./pages/Menu'));
const Feed = lazy(() => import('./pages/Feed'));
const FeedCategory = lazy(() => import('./pages/FeedCategory'));
const Chat = lazy(() => import('./pages/Chat'));
const ChannelView = lazy(() => import('./pages/ChannelView'));
const GroupView = lazy(() => import('./pages/GroupView'));
const Settings = lazy(() => import('./pages/Settings'));
const BugReport = lazy(() => import('./pages/BugReport'));
const BugReportDetail = lazy(() => import('./pages/BugReportDetail'));
const MyBugReports = lazy(() => import('./pages/MyBugReports'));
const Docs = lazy(() => import('./pages/Docs'));
const ControlPanel = lazy(() => import('./pages/ControlPanel'));
const StickerManager = lazy(() => import('./pages/StickerManager'));
const CreateUser = lazy(() => import('./pages/CreateUser'));
const EditUser = lazy(() => import('./pages/EditUser'));
const PermissionTemplates = lazy(() => import('./pages/PermissionTemplates'));
const GuestAuditLog = lazy(() => import('./pages/GuestAuditLog'));
const Trash = lazy(() => import('./pages/Trash'));
const SentryTest = lazy(() => import('./pages/SentryTest'));
const Monitoring = lazy(() => import('./pages/Monitoring'));
const CustomSectionPage = lazy(() => import('./pages/CustomSectionPage'));
const CustomFeedCategory = lazy(() => import('./pages/CustomFeedCategory'));
const SectionConstructor = lazy(() => import('./pages/SectionConstructor'));

const LOADING_SHELL_STYLE = {
  position: 'fixed',
  inset: 0,
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 9999,
};

const LOADING_SPINNER_STYLE = {
  width: 48,
  height: 48,
  border: '3px solid rgba(255,255,255,0.3)',
  borderTopColor: '#fff',
  borderRadius: '50%',
  animation: 'spin 1s linear infinite',
  margin: '0 auto 16px',
};

function PageLoader() {
  return (
    <div className="loading">
      <div className="spinner" />
    </div>
  );
}

function LazyPage({ children }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

function PrivateRoute({ children }) {
  const { isAuthenticated, loading } = useAuthStore();

  if (loading) {
    return (
      <div className="auth-loading-screen" style={LOADING_SHELL_STYLE}>
        <div className="auth-loading-content" style={{ textAlign: 'center', color: '#fff' }}>
          <div className="auth-spinner" style={LOADING_SPINNER_STYLE} />
          <p className="auth-loading-text" style={{ margin: 0, fontSize: 16, fontWeight: 500 }}>
            Загрузка…
          </p>
        </div>
      </div>
    );
  }

  // Если проверка завершена и пользователь не авторизован, редиректим на логин
  // Сохраняем текущий путь для возврата после логина
  if (!isAuthenticated && !loading) {
    return <Navigate to="/login" state={{ from: { pathname: window.location.pathname } }} replace />;
  }

  return children;
}

function SectionConstructorRoute({ children }) {
  const { isAuthenticated, loading, permissions } = useAuthStore();

  if (loading || (isAuthenticated && !permissions)) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (!permissions?.features?.sectionConstructor) {
    return <Navigate to="/" />;
  }

  return children;
}

function AdminRoute({ children }) {
  const { isAuthenticated, user, loading } = useAuthStore();

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (user?.role !== 'admin') {
    return <Navigate to="/" />;
  }

  return children;
}

function App() {
  const { verify, isAuthenticated, loading } = useAuthStore();
  // Мониторинг загрузки + авто-восстановление на WebKit (iOS)
  useAppBootMonitor();

  useEffect(() => {
    document.documentElement.dataset.appReady = '1';
    reportBootEvent('app_ready');
    markMilestone('react_mounted');
    // Приложение успешно стартовало — сбрасываем бюджет автоперезагрузок
    // (убираем _boot/_cb/_r из URL) и старые флаги.
    clearBootParamsFromUrl();
    try {
      sessionStorage.removeItem('boot_auto_reload_once');
      sessionStorage.removeItem('asset_auto_reload_once');
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    registerSessionExpiredHandler((message) => {
      useAuthStore.getState().handleSessionExpired(message);
    });
  }, []);

  useEffect(() => {
    const token = getStoredToken();
    const fastBoot = shouldUseFastBoot();

    if (token) {
      if (fastBoot) {
        useAuthStore.getState().fastBootFromToken();
      }
      verify({ background: fastBoot }).catch((err) => {
        console.error('❌ Verify error in App:', err);
        reportBootEvent('verify_failed', { message: err?.message || String(err), background: fastBoot });
        if (!fastBoot) useAuthStore.getState().forceBootstrapExit();
      });
    } else {
      useAuthStore.setState({ loading: false, isAuthenticated: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Мониторинг разрешения аутентификации
  useEffect(() => {
    if (!loading) {
      markMilestone('auth_resolved', { authenticated: isAuthenticated });
    }
  }, [loading, isAuthenticated]);

  // WebKit: setTimeout может не сработать минутами — при возврате на вкладку снимаем блокировку
  useEffect(() => {
    if (!shouldUseFastBoot()) return undefined;

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const { loading, isAuthenticated } = useAuthStore.getState();
      if (loading && !isAuthenticated) {
        reportBootEvent('webkit_fast_boot', { source: 'visibility_visible' });
        useAuthStore.getState().fastBootFromToken();
      }
    };

    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  // Единый вотчдог загрузки для всех браузеров (включая iOS):
  // не держим пустой экран дольше 15 с.
  // iOS Safari может «засыпать» таймеры, поэтому вотчдог срабатывает
  // при любом пробуждении вкладки (через visibilitychange выше).
  useEffect(() => {
    const token = getStoredToken();
    if (!token) return undefined;

    const watchdog = setTimeout(() => {
      const { loading: stillLoading, isAuthenticated: authed } = useAuthStore.getState();
      if (stillLoading && !authed) {
        const isWebKit = shouldUseFastBoot();
        reportBootEvent('boot_watchdog_timeout', { afterMs: 15000, webkit: isWebKit });
        useAuthStore.getState().forceBootstrapExit();
      }
    }, 15000);

    return () => clearTimeout(watchdog);
  }, []);

  return (
    <BrowserRouter>
      <InAppBrowserHint />
      <BootstrapRecovery />
      <Routes>
        <Route path="/login" element={<LazyPage><Login /></LazyPage>} />
        <Route
          path="/"
          element={
            <PrivateRoute>
              <Layout />
            </PrivateRoute>
          }
        >
          <Route index element={<LazyPage><Dashboard /></LazyPage>} />
          <Route path="menu" element={<LazyPage><Menu /></LazyPage>} />
          <Route path="chat" element={<LazyPage><Chat /></LazyPage>} />
          <Route path="chat/direct" element={<LazyPage><Chat /></LazyPage>} />
          <Route path="chat/channel/:channelId" element={<LazyPage><Chat /></LazyPage>} />
          <Route path="chat/group/:groupId" element={<LazyPage><Chat /></LazyPage>} />
          {/* Routes that require an open shift */}
          <Route element={<ShiftGuard />}>
            <Route element={<FeedGuard />}>
              <Route path="feed" element={<LazyPage><Feed /></LazyPage>} />
              <Route path="feed/category/:categoryKey" element={<LazyPage><FeedCategory /></LazyPage>} />
              <Route path="feed/category/:categoryKey/:itemId" element={<LazyPage><FeedCategory /></LazyPage>} />
              <Route path="feed/custom/:sectionKey" element={<LazyPage><CustomFeedCategory /></LazyPage>} />
              <Route path="feed/custom/:sectionKey/:itemId" element={<LazyPage><CustomFeedCategory /></LazyPage>} />
            </Route>
            <Route path="trash" element={<LazyPage><Trash /></LazyPage>} />
            <Route path="shifts" element={<AdminRoute><LazyPage><Shifts /></LazyPage></AdminRoute>} />
            <Route path="payment" element={<LazyPage><Payment /></LazyPage>} />
            <Route path="incidents" element={<LazyPage><Incidents /></LazyPage>} />
            <Route path="requests" element={<LazyPage><Requests /></LazyPage>} />
            <Route path="repairs" element={<LazyPage><Repairs /></LazyPage>} />
            <Route path="garbage" element={<LazyPage><Garbage /></LazyPage>} />
            <Route path="toilets" element={<LazyPage><Toilets /></LazyPage>} />
            <Route path="snow" element={<LazyPage><Snow /></LazyPage>} />
            <Route path="participants" element={<LazyPage><Participants /></LazyPage>} />
            <Route path="staff" element={<LazyPage><Staff /></LazyPage>} />
            <Route path="property" element={<LazyPage><Property /></LazyPage>} />
            <Route path="mounting" element={<LazyPage><Mounting /></LazyPage>} />
            <Route path="ormm" element={<LazyPage><ORMM /></LazyPage>} />
            <Route path="animation" element={<LazyPage><Animation /></LazyPage>} />
            <Route path="plants" element={<LazyPage><Plants /></LazyPage>} />
            <Route path="deratization" element={<LazyPage><Deratization /></LazyPage>} />
            <Route path="approvals" element={<LazyPage><Approvals /></LazyPage>} />
            <Route path="visitors" element={<LazyPage><Visitors /></LazyPage>} />
            <Route path="decor" element={<LazyPage><Decor /></LazyPage>} />
            <Route path="section/:sectionKey" element={<LazyPage><CustomSectionPage /></LazyPage>} />
            <Route path="journal" element={<LazyPage><Journal /></LazyPage>} />
          </Route>
          {/* Always accessible */}
          <Route path="settings" element={<LazyPage><Settings /></LazyPage>} />
          <Route path="bug-report" element={<LazyPage><BugReport /></LazyPage>} />
          <Route path="bug-reports/:id" element={<LazyPage><BugReportDetail /></LazyPage>} />
          <Route path="my-bugs" element={<LazyPage><MyBugReports /></LazyPage>} />
          <Route path="docs" element={<LazyPage><Docs /></LazyPage>} />
          {/* Hidden: Sentry test page — не ссылается ниоткуда */}
          <Route path="hidden/sentry-test" element={<LazyPage><SentryTest /></LazyPage>} />
          {/* Admin routes */}
          <Route
            path="admin/panel"
            element={
              <AdminRoute>
                <LazyPage><ControlPanel /></LazyPage>
              </AdminRoute>
            }
          />
          <Route
            path="admin/stickers"
            element={
              <AdminRoute>
                <LazyPage><StickerManager /></LazyPage>
              </AdminRoute>
            }
          />
          <Route
            path="admin/users/create"
            element={
              <AdminRoute>
                <LazyPage><CreateUser /></LazyPage>
              </AdminRoute>
            }
          />
          <Route
            path="admin/users/edit/:id"
            element={
              <AdminRoute>
                <LazyPage><EditUser /></LazyPage>
              </AdminRoute>
            }
          />
          <Route
            path="admin/permission-templates"
            element={
              <AdminRoute>
                <LazyPage><PermissionTemplates /></LazyPage>
              </AdminRoute>
            }
          />
          <Route
            path="admin/guest-audit"
            element={
              <AdminRoute>
                <LazyPage><GuestAuditLog /></LazyPage>
              </AdminRoute>
            }
          />
          <Route
            path="admin/trash"
            element={
              <AdminRoute>
                <LazyPage><Trash /></LazyPage>
              </AdminRoute>
            }
          />
          <Route
            path="admin/section-constructor"
            element={
              <SectionConstructorRoute>
                <LazyPage><SectionConstructor /></LazyPage>
              </SectionConstructorRoute>
            }
          />
          <Route
            path="admin/monitoring"
            element={
              <AdminRoute>
                <LazyPage><Monitoring /></LazyPage>
              </AdminRoute>
            }
          />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
